/**
 * Carvica Theme JS
 * Luxury house number signs & architectural signage
 *
 * Vanilla ES6+ — no frameworks.
 * Relies on `window.Carvica` global set in theme.liquid.
 */
;(function () {
  'use strict';

  /* ==========================================================================
     0.  UTILITIES
     ========================================================================== */

  /**
   * Format an amount in cents using the Shopify money format string stored in
   * `Carvica.moneyFormat`.  Handles {{amount}}, {{amount_no_decimals}},
   * {{amount_with_comma_separator}}, {{amount_no_decimals_with_comma_separator}},
   * and {{amount_with_apostrophe_separator}} placeholders.
   *
   * @param {number} cents  – price in the store's smallest currency unit
   * @returns {string}        formatted price string
   */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;

    const format = window.Carvica?.moneyFormat || '${{amount}}';

    function defaultTo(value, fallback) {
      return value == null || value !== value ? fallback : value; // eslint-disable-line no-self-compare
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultTo(precision, 2);
      thousands = defaultTo(thousands, ',');
      decimal   = defaultTo(decimal, '.');

      if (isNaN(number) || number == null) return '0';

      const fixedNumber = (number / 100.0).toFixed(precision);
      const parts       = fixedNumber.split('.');
      const dollars     = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
      const pennies     = parts[1] ? `${decimal}${parts[1]}` : '';

      return dollars + pennies;
    }

    let value = '';

    switch (format.match(/\{\{\s*(\w+)\s*\}\}/)?.[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2, ',', '.');
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, value);
  }

  /**
   * Simple debounce helper.
   */
  function debounce(fn, delay = 300) {
    let timer;
    return function (...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  /**
   * Fetch wrapper that returns parsed JSON and includes the Shopify-required
   * headers for AJAX API calls.
   */
  async function shopifyFetch(url, options = {}) {
    const defaults = {
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
    const response = await fetch(url, { ...defaults, ...options });
    if (!response.ok) throw new Error(`Shopify AJAX ${response.status}`);
    return response.json();
  }

  /* ==========================================================================
     1.  HEADER
     ========================================================================== */

  const Header = {
    selectors: {
      header:       '.site-header',
      mobileToggle: '[data-mobile-menu-toggle]',
      mobileMenu:   '[data-mobile-menu]',
      megaMenu:     '[data-mega-menu]',
      searchToggle: '[data-search-toggle]',
      searchBar:    '[data-search-bar]',
      cartCount:    '[data-cart-count]',
    },

    scrollThreshold: 50,

    init() {
      this.header = document.querySelector(this.selectors.header);
      if (!this.header) return;

      // Sticky header — add class on scroll
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });

      // Mobile hamburger toggle
      document.addEventListener('click', (e) => {
        const toggle = e.target.closest(this.selectors.mobileToggle);
        if (toggle) {
          e.preventDefault();
          this.toggleMobileMenu();
        }
      });

      // Mega menu hover/click
      document.querySelectorAll(this.selectors.megaMenu).forEach((trigger) => {
        trigger.addEventListener('mouseenter', () => this.openMegaMenu(trigger));
        trigger.addEventListener('mouseleave', () => this.closeMegaMenu(trigger));
        trigger.addEventListener('click', (e) => {
          if (window.innerWidth < 1024) {
            e.preventDefault();
            trigger.classList.toggle('is-open');
          }
        });
      });

      // Search toggle
      document.addEventListener('click', (e) => {
        if (e.target.closest(this.selectors.searchToggle)) {
          e.preventDefault();
          this.toggleSearch();
        }
      });

      // Initial cart count
      this.updateCartCount();
    },

    onScroll() {
      if (!this.header) return;
      const scrolled = window.scrollY > this.scrollThreshold;
      this.header.classList.toggle('is-scrolled', scrolled);
    },

    toggleMobileMenu() {
      const menu = document.querySelector(this.selectors.mobileMenu);
      if (!menu) return;
      const isOpen = menu.classList.toggle('is-open');
      document.body.classList.toggle('mobile-menu-open', isOpen);
      // Transparent header needs a solid backdrop while the menu is open
      this.header?.classList.toggle('has-menu-open', isOpen);

      // Toggle hamburger icon ARIA
      const toggle = document.querySelector(this.selectors.mobileToggle);
      if (toggle) toggle.setAttribute('aria-expanded', String(isOpen));
    },

    openMegaMenu(trigger) {
      trigger.classList.add('is-open');
    },

    closeMegaMenu(trigger) {
      trigger.classList.remove('is-open');
    },

    toggleSearch() {
      const bar = document.querySelector(this.selectors.searchBar);
      if (!bar) return;
      bar.classList.toggle('is-open');
      if (bar.classList.contains('is-open')) {
        const input = bar.querySelector('input[type="search"]');
        if (input) input.focus();
      }
    },

    /**
     * Read cart item count and update every [data-cart-count] badge.
     * Can be called with a number or will fetch from the JSON island.
     */
    updateCartCount(count) {
      if (count === undefined) {
        const el = document.getElementById('cart-icon-bubble');
        count = el ? parseInt(el.textContent, 10) || 0 : 0;
      }
      document.querySelectorAll(this.selectors.cartCount).forEach((badge) => {
        badge.textContent = count;
        badge.classList.toggle('is-hidden', count === 0);
      });
    },
  };

  /* ==========================================================================
     2.  CART DRAWER
     ========================================================================== */

  const CartDrawer = {
    selectors: {
      drawer:       '[data-cart-drawer]',
      overlay:      '[data-cart-drawer-overlay]',
      close:        '[data-cart-drawer-close]',
      toggle:       '[data-cart-drawer-toggle]',
      itemsWrap:    '[data-cart-items]',
      subtotal:     '[data-cart-subtotal]',
      emptyMsg:     '[data-cart-empty]',
      noteField:    '[data-cart-note]',
      crossSells:   '[data-cart-cross-sells]',
      trustBadges:  '[data-cart-trust-badges]',
    },

    init() {
      this.drawer = document.querySelector(this.selectors.drawer);
      if (!this.drawer) return;

      // Toggle open
      document.addEventListener('click', (e) => {
        if (e.target.closest(this.selectors.toggle)) {
          e.preventDefault();
          this.open();
        }
      });

      // Close button & overlay
      document.addEventListener('click', (e) => {
        if (
          e.target.closest(this.selectors.close) ||
          e.target.closest(this.selectors.overlay)
        ) {
          e.preventDefault();
          this.close();
        }
      });

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) this.close();
      });

      // Quantity change & remove (event delegation)
      this.drawer.addEventListener('click', (e) => {
        const qtyBtn = e.target.closest('[data-cart-qty]');
        if (qtyBtn) {
          e.preventDefault();
          const line = parseInt(qtyBtn.dataset.cartLine, 10);
          const delta = parseInt(qtyBtn.dataset.cartQty, 10); // +1 or -1
          this.changeLineQuantity(line, delta);
          return;
        }
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          const line = parseInt(removeBtn.dataset.cartLine, 10);
          this.removeItem(line);
        }
      });

      // Cart note — save on change
      const noteField = this.drawer.querySelector(this.selectors.noteField);
      if (noteField) {
        noteField.addEventListener(
          'change',
          debounce(() => this.saveNote(noteField.value), 500)
        );
      }
    },

    isOpen() {
      return this.drawer?.classList.contains('is-open');
    },

    async open() {
      if (!this.drawer) return;
      await this.fetchAndRender();
      this.drawer.classList.add('is-open');
      document.body.classList.add('cart-drawer-open');
      this.drawer.setAttribute('aria-hidden', 'false');
    },

    close() {
      if (!this.drawer) return;
      this.drawer.classList.remove('is-open');
      document.body.classList.remove('cart-drawer-open');
      this.drawer.setAttribute('aria-hidden', 'true');
    },

    /* ----- AJAX helpers ----- */

    async getCart() {
      return shopifyFetch('/cart.js');
    },

    /**
     * Add an item to the cart.
     * @param {number}  variantId   – Shopify variant ID
     * @param {number}  quantity    – quantity to add
     * @param {Object}  properties  – line item properties (customisations)
     */
    async addItem(variantId, quantity = 1, properties = {}) {
      const data = await shopifyFetch(window.Carvica.routes.cart_add_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ id: variantId, quantity, properties }),
      });
      await this.fetchAndRender();
      this.open();
      Toast.show(window.Carvica.strings.itemAdded || 'Added to cart');
      return data;
    },

    async addItems(items) {
      const data = await shopifyFetch(window.Carvica.routes.cart_add_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ items }),
      });
      await this.fetchAndRender();
      this.open();
      Toast.show(window.Carvica.strings.itemAdded || 'Added to cart');
      return data;
    },

    async changeLineQuantity(line, delta) {
      const cart = await this.getCart();
      const item = cart.items[line - 1];
      if (!item) return;
      const newQty = Math.max(0, item.quantity + delta);
      await shopifyFetch(window.Carvica.routes.cart_change_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity: newQty }),
      });
      await this.fetchAndRender();
    },

    async removeItem(line) {
      await shopifyFetch(window.Carvica.routes.cart_change_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ line, quantity: 0 }),
      });
      await this.fetchAndRender();
    },

    async saveNote(note) {
      await shopifyFetch(window.Carvica.routes.cart_update_url + '.js', {
        method: 'POST',
        body: JSON.stringify({ note }),
      });
    },

    /* ----- Render ----- */

    async fetchAndRender() {
      const cart = await this.getCart();
      this.renderItems(cart);
      this.renderSubtotal(cart);
      this.toggleFooter(cart);
      Header.updateCartCount(cart.item_count);

      // Update drawer header count
      const countEl = this.drawer.querySelector('[data-cart-drawer-count]');
      if (countEl) countEl.textContent = `(${cart.item_count})`;
    },

    /**
     * Toggle footer and empty-state visibility based on cart contents.
     */
    toggleFooter(cart) {
      const footer = this.drawer.querySelector('[data-cart-drawer-footer]');
      if (footer) footer.classList.toggle('cart-drawer__footer--hidden', cart.items.length === 0);
    },

    /**
     * Build the drawer item list from the AJAX cart JSON.
     * Class names must match cart-drawer.liquid CSS exactly.
     */
    renderItems(cart) {
      const wrap = this.drawer.querySelector(this.selectors.itemsWrap);
      const emptyMsg = this.drawer.querySelector(this.selectors.emptyMsg);
      if (!wrap) return;

      if (cart.items.length === 0) {
        wrap.innerHTML = '';
        wrap.classList.add('cart-drawer__items--hidden');
        if (emptyMsg) emptyMsg.classList.remove('cart-drawer__empty--hidden');
        return;
      }

      // Show items, hide empty message
      wrap.classList.remove('cart-drawer__items--hidden');
      if (emptyMsg) emptyMsg.classList.add('cart-drawer__empty--hidden');

      wrap.innerHTML = cart.items
        .map((item, idx) => {
          const line = idx + 1;
          const propsHtml = this.renderProperties(item.properties);
          const imgSrc = item.image
            ? item.image.replace(/(\.\w+)(\?.*)?$/, '_160x$1$2')
            : '';
          return `
            <div class="cart-drawer__item" data-cart-item data-line="${line}">
              <div class="cart-drawer__item-image">
                ${imgSrc ? `<a href="${item.url}" aria-label="${this.escapeHtml(item.product_title)}"><img src="${imgSrc}" alt="${this.escapeHtml(item.title)}" width="80" height="80" loading="lazy"></a>` : ''}
              </div>
              <div class="cart-drawer__item-info">
                <a href="${item.url}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a>
                ${item.variant_title ? `<p class="cart-drawer__item-variant">${this.escapeHtml(item.variant_title)}</p>` : ''}
                ${propsHtml}
                <div class="cart-drawer__item-bottom">
                  <div class="cart-drawer__item-quantity" data-cart-item-quantity>
                    <button type="button" class="cart-drawer__qty-btn" data-cart-qty="-1" data-cart-line="${line}" aria-label="Decrease quantity">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                    <input type="number" class="cart-drawer__qty-input" value="${item.quantity}" min="0" aria-label="Quantity" data-cart-qty-input readonly>
                    <button type="button" class="cart-drawer__qty-btn" data-cart-qty="1" data-cart-line="${line}" aria-label="Increase quantity">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    </button>
                  </div>
                  <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
                </div>
                <button type="button" class="cart-drawer__item-remove" data-cart-remove data-cart-line="${line}" aria-label="Remove ${this.escapeHtml(item.product_title)}">Remove</button>
              </div>
            </div>`;
        })
        .join('');
    },

    /**
     * Render line item properties (customisation details, uploaded files, etc.)
     * Uses <dl> structure matching cart-drawer.liquid CSS.
     * Underscore-prefixed keys are hidden (Shopify convention).
     */
    renderProperties(properties) {
      if (!properties || typeof properties !== 'object') return '';
      const entries = Object.entries(properties).filter(
        ([key, val]) => !key.startsWith('_') && val !== '' && val != null
      );
      if (entries.length === 0) return '';
      return `<dl class="cart-drawer__item-properties">
        ${entries
          .map(([key, val]) => {
            if (typeof val === 'string' && /\/uploads\//.test(val)) {
              return `<div class="cart-drawer__item-property"><dt>${this.escapeHtml(key)}:</dt><dd><a href="${this.escapeHtml(val)}" target="_blank" rel="noopener">Uploaded file</a></dd></div>`;
            }
            return `<div class="cart-drawer__item-property"><dt>${this.escapeHtml(key)}:</dt><dd>${this.escapeHtml(String(val))}</dd></div>`;
          })
          .join('')}
      </dl>`;
    },

    renderSubtotal(cart) {
      const el = this.drawer.querySelector(this.selectors.subtotal);
      if (el) el.textContent = formatMoney(cart.total_price);
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },
  };

  /* ==========================================================================
     3.  PRODUCT VARIANT SELECTOR
     ========================================================================== */

  const VariantSelector = {
    selectors: {
      form:        '[data-product-form]',
      variantJson: '[data-product-variants-json]',
      priceEl:     '[data-product-price]',
      compareEl:   '[data-product-compare-price]',
      skuEl:       '[data-product-sku]',
      addBtn:      '[data-add-to-cart]',
      addBtnText:  '[data-add-to-cart-text]',
      optionInput: '[data-option-input]',
      variantIdInput: '[name="id"]',
    },

    init() {
      this.forms = document.querySelectorAll(this.selectors.form);
      if (!this.forms.length) return;

      this.forms.forEach((form) => {
        const jsonEl = form.querySelector(this.selectors.variantJson);
        if (!jsonEl) return;

        let variants;
        try {
          variants = JSON.parse(jsonEl.textContent);
        } catch {
          return;
        }

        form._carvicaVariants = variants;

        // Listen for option changes (radio, select, etc.)
        form.addEventListener('change', (e) => {
          if (e.target.closest(this.selectors.optionInput)) {
            this.onOptionChange(form);
          }
        });
      });
    },

    /**
     * When any option input changes, recalculate the selected variant and
     * update price / availability / images / URL.
     */
    onOptionChange(form) {
      const variants = form._carvicaVariants;
      if (!variants) return;

      // Liquid stamps `--active` on the initially selected value; drop it once the
      // customer picks something else, otherwise two values look selected at once.
      form.querySelectorAll('[data-option-btn], [data-option-swatch]').forEach((el) => {
        el.classList.remove('product__option-btn--active', 'product__color-swatch--active');
      });

      // Gather selected option values (option1, option2, option3)
      const options = [];
      form.querySelectorAll('fieldset[data-product-option]').forEach((fieldset) => {
        const idx = parseInt(fieldset.dataset.optionIndex, 10);
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        const select = fieldset.querySelector('select');
        if (checked) {
          options[idx] = checked.value;
        } else if (select) {
          options[idx] = select.value;
        }
      });

      // Find matching variant
      const variant = variants.find((v) =>
        options.every((opt, i) => v[`option${i + 1}`] === opt)
      );

      // Re-apply the active class to whatever is now checked, so the selected
      // state is correct even without :has() support.
      form.querySelectorAll('[data-option-input]:checked').forEach((input) => {
        const btn = input.closest('[data-option-btn]');
        const sw  = input.closest('[data-option-swatch]');
        if (btn) btn.classList.add('product__option-btn--active');
        if (sw && sw.classList.contains('product__color-swatch')) {
          sw.classList.add('product__color-swatch--active');
        }
      });

      this.updateUI(form, variant);
    },

    updateUI(form, variant) {
      // The price block sits OUTSIDE the form, so scope to the product section
      // (or the document) rather than the form itself.
      const root       = form.closest('[data-product-page]') || document;
      const priceEl    = root.querySelector(this.selectors.priceEl);
      const compareEl  = root.querySelector(this.selectors.compareEl) || priceEl?.querySelector('.price__compare');
      const addBtn     = form.querySelector(this.selectors.addBtn);
      const addBtnText = form.querySelector(this.selectors.addBtnText);
      const idInput    = form.querySelector(this.selectors.variantIdInput);

      if (!variant) {
        // No matching variant — unavailable
        if (addBtn)     addBtn.disabled = true;
        if (addBtnText) addBtnText.textContent = window.Carvica.strings.unavailable;
        return;
      }

      // Update hidden variant ID input
      if (idInput) idInput.value = variant.id;

      // Price — target the inner span so we don't wipe the snippet's markup
      if (priceEl) {
        const target = priceEl.querySelector('.price__regular') || priceEl;
        target.textContent = formatMoney(variant.price);
        priceEl.querySelector('.price__label')?.remove();   // no longer a "From" price
      }
      // Keep the sticky mobile bar in sync
      const stickyPrice = document.querySelector('.sticky-atc__price');
      if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);

      // SKU — also lives outside the form, so resolve it from the section root
      const skuEl = root.querySelector(this.selectors.skuEl);
      if (skuEl) {
        if (variant.sku) {
          skuEl.textContent = `SKU: ${variant.sku}`;
          skuEl.hidden = false;
        } else {
          skuEl.hidden = true;   // nothing useful to show for this variant
        }
      }
      if (compareEl) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          const c = compareEl.querySelector('s') || compareEl;
          c.textContent = formatMoney(variant.compare_at_price);
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      // Availability
      if (variant.available) {
        if (addBtn)     addBtn.disabled = false;
        if (addBtnText) addBtnText.textContent = window.Carvica.strings.addToCart;
      } else {
        if (addBtn)     addBtn.disabled = true;
        if (addBtnText) addBtnText.textContent = window.Carvica.strings.soldOut;
      }

      // Update URL with variant id (no page reload)
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // Dispatch custom event so gallery / configurator can react
      document.dispatchEvent(
        new CustomEvent('carvica:variant:change', { detail: { variant, form } })
      );
    },
  };

  /* ==========================================================================
     4.  PRODUCT TABS
     ========================================================================== */

  const ProductTabs = {
    selectors: {
      tabNav:   '[data-tab-nav]',
      tabBtn:   '[data-tab-button]',
      tabPanel: '[data-tab-panel]',
    },

    init() {
      document.addEventListener('click', (e) => {
        const btn = e.target.closest(this.selectors.tabBtn);
        if (!btn) return;
        e.preventDefault();

        const parent = btn.closest(this.selectors.tabNav)?.parentElement;
        if (!parent) return;

        const target = btn.dataset.tabButton;

        // Deactivate all
        parent.querySelectorAll(this.selectors.tabBtn).forEach((b) => {
          // clear both the Liquid-rendered initial class and the JS one
          b.classList.remove('is-active', 'product__tab-btn--active');
          b.setAttribute('aria-selected', 'false');
        });
        parent.querySelectorAll(this.selectors.tabPanel).forEach((p) => {
          p.classList.remove('is-active', 'product__tab-panel--active');
          p.setAttribute('aria-hidden', 'true');
          p.hidden = false; // never leave a stale hidden attribute behind
        });

        // Activate target
        btn.classList.add('is-active', 'product__tab-btn--active');
        btn.setAttribute('aria-selected', 'true');
        const panel = parent.querySelector(`[data-tab-panel="${target}"]`);
        if (panel) {
          panel.classList.add('is-active', 'product__tab-panel--active');
          panel.setAttribute('aria-hidden', 'false');
          panel.hidden = false;
        }
      });
    },
  };

  /* ==========================================================================
     5.  PRODUCT GALLERY
     ========================================================================== */

  const ProductGallery = {
    selectors: {
      gallery:    '[data-product-gallery]',
      mainImage:  '[data-gallery-main]',
      thumbs:     '[data-gallery-thumb]',
      lightbox:   '[data-gallery-lightbox]',
      lightboxImg:'[data-gallery-lightbox-img]',
      lightboxClose:'[data-gallery-lightbox-close]',
    },

    init() {
      const gallery = document.querySelector(this.selectors.gallery);
      if (!gallery) return;

      this.mainImage = gallery.querySelector(this.selectors.mainImage);
      // Grid mode: every image is shown at once; clicking one opens the lightbox.
      this.isGrid = gallery.hasAttribute('data-gallery-grid');

      gallery.addEventListener('click', (e) => {
        const thumb = e.target.closest(this.selectors.thumbs);
        if (thumb) {
          e.preventDefault();
          if (this.isGrid) {
            this.openLightboxWith(thumb.dataset.galleryThumb, thumb.dataset.galleryAlt || '');
            return;
          }
          this.swapMainImage(thumb.dataset.galleryThumb, thumb.dataset.galleryAlt || '');
          gallery.querySelectorAll(this.selectors.thumbs).forEach((t) =>
            t.classList.remove('is-active')
          );
          thumb.classList.add('is-active');
          return;
        }

        // Main image click — open lightbox (non-grid layouts)
        if (!this.isGrid && e.target.closest(this.selectors.mainImage)) {
          this.openLightbox();
        }
      });

      // Lightbox close
      document.addEventListener('click', (e) => {
        if (
          e.target.closest(this.selectors.lightboxClose) ||
          (e.target.closest(this.selectors.lightbox) && !e.target.closest(this.selectors.lightboxImg))
        ) {
          this.closeLightbox();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.closeLightbox();
      });

      this.gallery = gallery;

      // React to variant change: swap the main image and filter thumbnails by colour.
      document.addEventListener('carvica:variant:change', (e) => {
        const variant = e.detail.variant;
        const form = e.detail.form;
        const colorInput = form?.querySelector('[data-color-input]:checked');
        const color = colorInput ? colorInput.value : null;

        if (color) this.filterByColor(color);

        if (!this.isGrid && variant.featured_image?.src) {
          this.swapMainImage(variant.featured_image.src, variant.featured_image.alt || '');
        }
      });

      // Apply the initial colour filter on page load (for the pre-selected variant).
      const initialColor = gallery
        .closest('[data-product-section]')
        ?.querySelector('[data-color-input]:checked')
        || document.querySelector('[data-color-input]:checked');
      if (initialColor) this.filterByColor(initialColor.value);
    },

    /**
     * Show only the thumbnails belonging to the selected colour (plus any
     * ungrouped/shared images), and switch the main image to the first match.
     * If no images are colour-grouped, everything stays visible.
     */
    filterByColor(colorValue) {
      if (!this.gallery) return;
      const cv = String(colorValue || '').toLowerCase().trim();
      const thumbs = Array.from(this.gallery.querySelectorAll(this.selectors.thumbs));
      if (!thumbs.length) return;

      // Are any thumbnails actually grouped by colour? If not, do nothing.
      const hasGroups = thumbs.some((t) => (t.dataset.galleryColor || '').trim() !== '');
      if (!hasGroups) return;

      let firstMatch = null;
      thumbs.forEach((t) => {
        const c = (t.dataset.galleryColor || '').toLowerCase().trim();
        const show = c === '' || c === cv; // shared images always show
        t.style.display = show ? '' : 'none';
        if (!firstMatch && c === cv) firstMatch = t;
      });

      if (!firstMatch) return;

      if (this.isGrid) {
        // Bring the first image of the selected colour into view.
        this.gallery.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }

      this.swapMainImage(firstMatch.dataset.galleryThumb, firstMatch.dataset.galleryAlt || '');
      thumbs.forEach((t) => t.classList.remove('is-active', 'product__thumbnail--active'));
      firstMatch.classList.add('is-active', 'product__thumbnail--active');
    },

    swapMainImage(src, alt) {
      if (!this.mainImage) return;
      const img = this.mainImage.querySelector('img') || this.mainImage;
      if (img.tagName === 'IMG') {
        img.src = src;
        img.alt = alt;
      } else {
        const child = img.querySelector('img');
        if (child) {
          child.src = src;
          child.alt = alt;
        }
      }
    },

    /** Open the lightbox showing a specific image. */
    openLightboxWith(src, alt) {
      const lb = document.querySelector(this.selectors.lightbox);
      if (!lb || !src) return;
      const lbImg = lb.querySelector(this.selectors.lightboxImg);
      if (lbImg) {
        lbImg.src = src;
        lbImg.alt = alt || '';
      }
      lb.classList.add('is-open');
      lb.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lightbox-open');
    },

    openLightbox() {
      const lb = document.querySelector(this.selectors.lightbox);
      if (!lb) return;
      const lbImg = lb.querySelector(this.selectors.lightboxImg);
      const mainImg = this.mainImage?.querySelector('img') || this.mainImage;
      if (lbImg && mainImg) {
        lbImg.src = mainImg.src?.replace(/_\d+x\d*\./, '.') || mainImg.src;
        lbImg.alt = mainImg.alt || '';
      }
      lb.classList.add('is-open');
      lb.setAttribute('aria-hidden', 'false');
      document.body.classList.add('lightbox-open');
    },

    closeLightbox() {
      const lb = document.querySelector(this.selectors.lightbox);
      if (!lb) return;
      lb.classList.remove('is-open');
      lb.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('lightbox-open');
    },
  };

  /* ==========================================================================
     6.  ACCORDION / FAQ
     ========================================================================== */

  const Accordion = {
    selectors: {
      item:    '[data-accordion-item]',
      trigger: '[data-accordion-trigger]',
      content: '[data-accordion-content]',
    },

    init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest(this.selectors.trigger);
        if (!trigger) return;
        e.preventDefault();

        const item = trigger.closest(this.selectors.item);
        if (!item) return;

        const content = item.querySelector(this.selectors.content);
        if (!content) return;

        const isOpen = item.classList.contains('is-open');

        if (isOpen) {
          // Collapse with smooth height animation
          content.style.height = `${content.scrollHeight}px`;
          // Force reflow
          content.offsetHeight; // eslint-disable-line no-unused-expressions
          content.style.height = '0';
          content.addEventListener(
            'transitionend',
            () => {
              item.classList.remove('is-open');
              content.style.height = '';
              trigger.setAttribute('aria-expanded', 'false');
            },
            { once: true }
          );
        } else {
          // Expand
          item.classList.add('is-open');
          content.style.height = '0';
          // Force reflow
          content.offsetHeight; // eslint-disable-line no-unused-expressions
          content.style.height = `${content.scrollHeight}px`;
          content.addEventListener(
            'transitionend',
            () => {
              content.style.height = '';
              trigger.setAttribute('aria-expanded', 'true');
            },
            { once: true }
          );
        }
      });
    },
  };

  /* ==========================================================================
     7.  NEWSLETTER FORM
     ========================================================================== */

  const NewsletterForm = {
    selectors: {
      form:    '[data-newsletter-form]',
      message: '[data-newsletter-message]',
    },

    init() {
      document.querySelectorAll(this.selectors.form).forEach((form) => {
        form.addEventListener('submit', (e) => this.onSubmit(e, form));
      });
    },

    async onSubmit(e, form) {
      e.preventDefault();

      const messageEl = form.querySelector(this.selectors.message);
      const email = form.querySelector('input[type="email"]')?.value;

      if (!email) return;

      try {
        // Shopify customer newsletter endpoint (or use Klaviyo / mailing list endpoint)
        const formData = new FormData(form);
        const response = await fetch(form.action || '/contact', {
          method: 'POST',
          body: formData,
          headers: { Accept: 'application/json' },
        });

        if (response.ok) {
          if (messageEl) {
            messageEl.textContent = 'Thank you for subscribing!';
            messageEl.classList.add('is-success');
            messageEl.classList.remove('is-error');
          }
          form.reset();
        } else {
          throw new Error('Subscription failed');
        }
      } catch {
        if (messageEl) {
          messageEl.textContent = 'Something went wrong. Please try again.';
          messageEl.classList.add('is-error');
          messageEl.classList.remove('is-success');
        }
      }
    },
  };

  /* ==========================================================================
     8.  QUICK VIEW
     ========================================================================== */

  const QuickView = {
    selectors: {
      trigger:  '[data-quick-view]',
      modal:    '[data-quick-view-modal]',
      content:  '[data-quick-view-content]',
      close:    '[data-quick-view-close]',
      overlay:  '[data-quick-view-overlay]',
    },

    init() {
      document.addEventListener('click', (e) => {
        const trigger = e.target.closest(this.selectors.trigger);
        if (trigger) {
          e.preventDefault();
          const handle = trigger.dataset.quickView;
          if (handle) this.open(handle);
        }

        if (
          e.target.closest(this.selectors.close) ||
          e.target.closest(this.selectors.overlay)
        ) {
          this.close();
        }
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.close();
      });
    },

    async open(handle) {
      const modal   = document.querySelector(this.selectors.modal);
      const content = document.querySelector(this.selectors.content);
      if (!modal || !content) return;

      content.innerHTML = '<div class="quick-view__loading">Loading&hellip;</div>';
      modal.classList.add('is-open');
      document.body.classList.add('quick-view-open');

      try {
        const product = await shopifyFetch(`/products/${handle}.js`);
        content.innerHTML = this.renderProduct(product);
        // Re-bind variant selector for the quick view form
        VariantSelector.init();
      } catch {
        content.innerHTML = '<p class="quick-view__error">Unable to load product.</p>';
      }
    },

    close() {
      const modal = document.querySelector(this.selectors.modal);
      if (!modal) return;
      modal.classList.remove('is-open');
      document.body.classList.remove('quick-view-open');
    },

    renderProduct(product) {
      const variant = product.variants[0];
      const image = product.images[0] || '';
      const optionsHtml = product.options
        .map(
          (opt, i) => `
          <div class="quick-view__option">
            <label>${this.escapeHtml(opt.name)}</label>
            <select data-product-option data-option-position="${i + 1}">
              ${opt.values
                .map((v) => `<option value="${this.escapeHtml(v)}">${this.escapeHtml(v)}</option>`)
                .join('')}
            </select>
          </div>`
        )
        .join('');

      return `
        <div class="quick-view__grid">
          <div class="quick-view__image">
            <img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy">
          </div>
          <div class="quick-view__info">
            <h2 class="quick-view__title">${this.escapeHtml(product.title)}</h2>
            <p class="quick-view__price" data-product-price>${formatMoney(variant.price)}</p>
            <form data-product-form>
              <script type="application/json" data-product-variants-json>${JSON.stringify(product.variants)}</script>
              ${optionsHtml}
              <input type="hidden" name="id" value="${variant.id}">
              <button type="submit" class="btn btn--primary" data-add-to-cart ${!variant.available ? 'disabled' : ''}>
                <span data-add-to-cart-text>${variant.available ? window.Carvica.strings.addToCart : window.Carvica.strings.soldOut}</span>
              </button>
            </form>
          </div>
        </div>`;
    },

    escapeHtml(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    },
  };

  /* ==========================================================================
     9.  ANNOUNCEMENT BAR
     ========================================================================== */

  const AnnouncementBar = {
    selectors: {
      bar:   '.announcement-bar',
      close: '[data-announcement-close]',
    },

    storageKey: 'carvica_announcement_dismissed',

    init() {
      const bar = document.querySelector(this.selectors.bar);
      if (!bar) return;

      // Already dismissed this session?
      if (sessionStorage.getItem(this.storageKey)) {
        bar.remove();
        return;
      }

      document.addEventListener('click', (e) => {
        if (e.target.closest(this.selectors.close)) {
          e.preventDefault();
          bar.classList.add('is-dismissed');
          bar.addEventListener('transitionend', () => bar.remove(), { once: true });
          sessionStorage.setItem(this.storageKey, '1');
        }
      });
    },
  };

  /* ==========================================================================
     10. LAZY LOADING
     ========================================================================== */

  const LazyLoad = {
    init() {
      if (!('IntersectionObserver' in window)) {
        // Fallback: load all immediately
        document.querySelectorAll('img[data-src]').forEach((img) => this.load(img));
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.load(entry.target);
              this.observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '200px' }
      );

      document.querySelectorAll('img[data-src]').forEach((img) => {
        this.observer.observe(img);
      });
    },

    load(img) {
      if (img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute('data-src');
      }
      if (img.dataset.srcset) {
        img.srcset = img.dataset.srcset;
        img.removeAttribute('data-srcset');
      }
      img.classList.add('is-loaded');
    },
  };

  /* ==========================================================================
     11. SMOOTH SCROLL
     ========================================================================== */

  const SmoothScroll = {
    init() {
      document.addEventListener('click', (e) => {
        const link = e.target.closest('a[href^="#"]');
        if (!link) return;

        const hash = link.getAttribute('href');
        if (!hash || hash === '#') return;

        const target = document.querySelector(hash);
        if (!target) return;

        e.preventDefault();

        const headerOffset = document.querySelector('.site-header')?.offsetHeight || 0;
        const elementPosition = target.getBoundingClientRect().top + window.scrollY;

        window.scrollTo({
          top: elementPosition - headerOffset - 20,
          behavior: 'smooth',
        });

        // Update URL hash without jump
        window.history.pushState(null, '', hash);
      });
    },
  };

  /* ==========================================================================
     12. TOAST NOTIFICATIONS
     ========================================================================== */

  const Toast = {
    container: null,
    timeout: null,

    init() {
      // Create toast container if missing
      if (!document.querySelector('[data-toast-container]')) {
        const div = document.createElement('div');
        div.classList.add('toast-container');
        div.setAttribute('data-toast-container', '');
        div.setAttribute('aria-live', 'polite');
        document.body.appendChild(div);
      }
      this.container = document.querySelector('[data-toast-container]');
    },

    /**
     * Display a toast notification.
     * @param {string} message
     * @param {string} type – 'success' | 'error' | 'info'
     * @param {number} duration – ms before auto-dismiss (default 3500)
     */
    show(message, type = 'success', duration = 3500) {
      if (!this.container) this.init();

      const toast = document.createElement('div');
      toast.classList.add('toast', `toast--${type}`);
      toast.textContent = message;

      this.container.appendChild(toast);

      // Trigger enter animation
      requestAnimationFrame(() => toast.classList.add('is-visible'));

      // Auto dismiss
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => {
        toast.classList.remove('is-visible');
        toast.addEventListener('transitionend', () => toast.remove(), { once: true });
      }, duration);
    },
  };

  /* ==========================================================================
     13. CURRENCY SELECTOR
     ========================================================================== */

  const CurrencySelector = {
    selectors: {
      form:   '[data-currency-form]',
      select: '[data-currency-select]',
    },

    init() {
      document.addEventListener('change', (e) => {
        const select = e.target.closest(this.selectors.select);
        if (!select) return;

        const form = select.closest(this.selectors.form);
        if (form) {
          // Shopify Markets currency switching uses a form submission
          form.submit();
        }
      });
    },
  };

  /* ==========================================================================
     14. MOBILE STICKY ADD TO CART
     ========================================================================== */

  const MobileStickyATC = {
    selectors: {
      bar:     '[data-sticky-atc]',
      trigger: '[data-add-to-cart]', // watches original ATC button visibility
    },

    init() {
      this.bar = document.querySelector(this.selectors.bar);
      if (!this.bar) return;

      const triggerBtn = document.querySelector(
        `${this.selectors.trigger}:not([data-sticky-atc] ${this.selectors.trigger})`
      );
      if (!triggerBtn) return;

      if (!('IntersectionObserver' in window)) return;

      // Show sticky bar when the original ATC button scrolls out of view
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.bar.classList.toggle('sticky-atc--visible', !entry.isIntersecting);
        },
        { threshold: 0 }
      );

      this.observer.observe(triggerBtn);
    },
  };

  /* ==========================================================================
     15. COLLECTION FILTERS
     ========================================================================== */

  const CollectionFilters = {
    selectors: {
      sidebar:  '[data-filter-sidebar]',
      toggle:   '[data-filter-toggle]',
      close:    '[data-filter-close]',
      form:     '[data-filter-form]',
      overlay:  '[data-filter-overlay]',
    },

    init() {
      // Toggle filter sidebar on mobile
      document.addEventListener('click', (e) => {
        if (e.target.closest(this.selectors.toggle)) {
          e.preventDefault();
          this.openSidebar();
        }
        if (
          e.target.closest(this.selectors.close) ||
          e.target.closest(this.selectors.overlay)
        ) {
          e.preventDefault();
          this.closeSidebar();
        }
      });

      // Handle filter form changes — submit via AJAX or standard navigation
      document.querySelectorAll(this.selectors.form).forEach((form) => {
        form.addEventListener('change', debounce(() => this.applyFilters(form), 500));
      });
    },

    openSidebar() {
      const sidebar = document.querySelector(this.selectors.sidebar);
      if (sidebar) {
        sidebar.classList.add('is-open');
        document.body.classList.add('filter-sidebar-open');
      }
    },

    closeSidebar() {
      const sidebar = document.querySelector(this.selectors.sidebar);
      if (sidebar) {
        sidebar.classList.remove('is-open');
        document.body.classList.remove('filter-sidebar-open');
      }
    },

    /**
     * Collect all active filter values and update the URL / reload the page.
     * Shopify Online Store 2.0 uses URL-based filtering with the Section
     * Rendering API; for simplicity we navigate to the built URL.
     */
    applyFilters(form) {
      const formData = new FormData(form);
      const params = new URLSearchParams();

      for (const [key, value] of formData.entries()) {
        if (value) params.append(key, value);
      }

      const url = `${window.location.pathname}?${params.toString()}`;
      window.location.href = url;
    },
  };

  /* ==========================================================================
     16. ADD-TO-CART FORM HANDLER
     ========================================================================== */

  const AddToCartForm = {
    // Mounting Type is a line item property and cannot carry a price, so the
    // surcharged option is a real product added as a second cart line.
    surchargeFor(form) {
      const sel = form.querySelector('[data-mounting-select]');
      if (!sel) return null;
      const variant = parseInt(sel.dataset.addonVariant, 10);
      const trigger = (sel.dataset.addonTrigger || '').trim();
      if (!variant || !trigger) return null;
      if (sel.value.trim() !== trigger) return null;
      return variant;
    },

    init() {
      document.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-product-form]');
        if (!form) return;

        // A chosen file can't travel in a JSON cart request. Let the browser do a
        // native multipart POST to /cart/add instead — Shopify stores the upload
        // as a line item property. Everything else still goes through AJAX.
        const hasFile = [...form.querySelectorAll('input[type="file"][name^="properties["]')]
          .some((i) => i.files && i.files.length);
        if (hasFile) {
          // The native multipart POST can only create one line item. Add the
          // surcharge first, then let the browser submit the sign itself.
          const addon = this.surchargeFor(form);
          if (!addon || form.dataset.surchargeAdded) return;
          e.preventDefault();
          const btn = form.querySelector('[data-add-to-cart]');
          if (btn) btn.disabled = true;
          shopifyFetch(window.Carvica.routes.cart_add_url + '.js', {
            method: 'POST',
            body: JSON.stringify({ id: addon, quantity: 1 }),
          })
            .catch(() => {})
            .finally(() => {
              form.dataset.surchargeAdded = '1';
              form.submit();
            });
          return;
        }

        e.preventDefault();

        const variantId = form.querySelector('[name="id"]')?.value;
        const quantity  = parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1;

        if (!variantId) return;

        // Gather line item properties (personalisation fields)
        const properties = {};
        form.querySelectorAll('[name^="properties["]').forEach((input) => {
          const match = input.name.match(/properties\[(.+?)\]/);
          if (!match) return;
          // Skip unchecked checkboxes / radios
          if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
          // File inputs are handled by the native-submit path above
          if (input.type === 'file') return;
          // Skip empty values
          if (!input.value) return;
          properties[match[1]] = input.value;
        });

        // Disable button while adding
        const btn = form.querySelector('[data-add-to-cart]');
        if (btn) btn.disabled = true;

        const items = [{ id: parseInt(variantId, 10), quantity, properties }];
        const addon = this.surchargeFor(form);
        if (addon) items.push({ id: addon, quantity });

        CartDrawer.addItems(items)
          .catch(() => Toast.show('Could not add to cart. Please try again.', 'error'))
          .finally(() => {
            if (btn) btn.disabled = false;
          });
      });
    },
  };

  /* ==========================================================================
     INIT — DOMContentLoaded
     ========================================================================== */

  /* ==========================================================================
     17. PRODUCT QUANTITY BUTTONS
     ========================================================================== */

  /* ==========================================================================
     18. SCROLL REVEAL (editorial)
     ========================================================================== */

  const ScrollReveal = {
    init() {
      const els = document.querySelectorAll('.reveal');
      if (!els.length) return;

      // No IntersectionObserver / reduced motion -> show everything immediately.
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce || !('IntersectionObserver' in window)) {
        els.forEach((el) => el.classList.add('is-revealed'));
        return;
      }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
      );

      els.forEach((el) => io.observe(el));
    },
  };

  /* ==========================================================================
     19. EDITORIAL RAIL (horizontal scroller)
     ========================================================================== */

  const Rail = {
    init() {
      document.querySelectorAll('[data-rail]').forEach((rail) => {
        const wrap = rail.closest('[data-rail-wrap]') || rail.parentElement;
        const prev = wrap?.querySelector('[data-rail-prev]');
        const next = wrap?.querySelector('[data-rail-next]');
        if (!prev && !next) return;

        const step = () => {
          const first = rail.firstElementChild;
          if (!first) return rail.clientWidth * 0.8;
          const gap = parseFloat(getComputedStyle(rail).columnGap || '20') || 20;
          return first.getBoundingClientRect().width + gap;
        };

        const sync = () => {
          const max = rail.scrollWidth - rail.clientWidth - 2;
          if (prev) prev.disabled = rail.scrollLeft <= 2;
          if (next) next.disabled = rail.scrollLeft >= max;
        };

        prev?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: 'smooth' }));
        next?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: 'smooth' }));
        rail.addEventListener('scroll', sync, { passive: true });
        window.addEventListener('resize', sync);
        sync();
      });
    },
  };

  /* ==========================================================================
     20. STICKY SCROLL STORY
     ========================================================================== */

  const StickyStory = {
    init() {
      document.querySelectorAll('[data-sticky-story]').forEach((root) => {
        const steps  = [...root.querySelectorAll('[data-story-step]')];
        const layers = [...root.querySelectorAll('[data-story-layer]')];
        const dots   = [...root.querySelectorAll('[data-story-dot]')];
        if (steps.length < 2) return;

        const activate = (idx) => {
          steps.forEach((el, i)  => el.classList.toggle('is-active', i === idx));
          layers.forEach((el, i) => el.classList.toggle('is-active', i === idx));
          dots.forEach((el, i)   => el.classList.toggle('is-active', i === idx));
        };

        if (!('IntersectionObserver' in window)) { activate(0); return; }

        // Activate whichever step is nearest the middle of the viewport.
        const io = new IntersectionObserver(
          (entries) => {
            let best = null;
            entries.forEach((e) => {
              if (!e.isIntersecting) return;
              if (!best || e.intersectionRatio > best.intersectionRatio) best = e;
            });
            if (!best) return;
            const idx = parseInt(best.target.dataset.storyStep, 10);
            if (!Number.isNaN(idx)) activate(idx);
          },
          { rootMargin: '-42% 0px -42% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
        );

        steps.forEach((el) => io.observe(el));
      });
    },
  };

  /* ==========================================================================
     21. COUNT-UP NUMBERS
     ========================================================================== */

  const CountUp = {
    init() {
      const nums = document.querySelectorAll('[data-count-to]');
      if (!nums.length) return;

      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const render = (el, value, decimals) => {
        el.textContent = value.toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        });
      };

      const run = (el) => {
        const target   = parseFloat(el.dataset.countTo) || 0;
        const decimals = parseInt(el.dataset.countDecimals, 10) || 0;
        if (reduce) return render(el, target, decimals);

        const duration = 1600;
        const start    = performance.now();
        const tick = (now) => {
          const p = Math.min((now - start) / duration, 1);
          // easeOutExpo
          const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
          render(el, target * eased, decimals);
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      };

      if (!('IntersectionObserver' in window)) { nums.forEach(run); return; }

      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            run(e.target);
            io.unobserve(e.target);
          });
        },
        { threshold: 0.4 }
      );
      nums.forEach((el) => io.observe(el));
    },
  };

  /* ==========================================================================
     22. PARALLAX
     ========================================================================== */

  const Parallax = {
    init() {
      const els = [...document.querySelectorAll('[data-parallax]')];
      if (!els.length) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      let ticking = false;
      const visible = new Set();

      const update = () => {
        ticking = false;
        const vh = window.innerHeight;
        visible.forEach((el) => {
          const strength = (parseFloat(el.dataset.parallaxStrength) || 15) / 100;
          const r = el.getBoundingClientRect();
          // -1 (below viewport) .. 1 (above viewport)
          const progress = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
          const shift = -progress * strength * 100;
          el.style.transform = `translate3d(0, ${shift.toFixed(2)}px, 0)`;
        });
      };

      const onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((e) => (e.isIntersecting ? visible.add(e.target) : visible.delete(e.target)));
            onScroll();
          },
          { rootMargin: '100px 0px' }
        );
        els.forEach((el) => io.observe(el));
      } else {
        els.forEach((el) => visible.add(el));
      }

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
      onScroll();
    },
  };

  /* ==========================================================================
     23. MARQUEE SPEED
     ========================================================================== */

  const Marquee = {
    init() {
      document.querySelectorAll('[data-marquee]').forEach((track) => {
        const secs = parseFloat(track.dataset.marqueeSpeed) || 40;
        track.style.setProperty('--marquee-duration', `${secs}s`);
      });
    },
  };

  /* ==========================================================================
     24. HERO CAROUSEL (cross-fade, autoplay)
     ========================================================================== */

  const HeroCarousel = {
    init() {
      document.querySelectorAll('[data-hero-carousel]').forEach((root) => {
        const slides = [...root.querySelectorAll('[data-hero-slide]')];
        const dots   = [...root.querySelectorAll('[data-hero-dot]')];
        if (slides.length < 2) return;

        const interval = parseInt(root.dataset.heroInterval, 10) || 6000;
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        let index = 0;
        let timer = null;

        const show = (next) => {
          index = (next + slides.length) % slides.length;
          slides.forEach((el, i) => {
            const on = i === index;
            el.classList.toggle('is-active', on);
            if (on) el.removeAttribute('aria-hidden');
            else el.setAttribute('aria-hidden', 'true');
          });
          dots.forEach((d, i) => {
            d.classList.toggle('is-active', i === index);
            d.setAttribute('aria-selected', i === index ? 'true' : 'false');
          });
        };

        const start = () => {
          if (reduce || timer) return;
          timer = setInterval(() => show(index + 1), interval);
        };
        const stop = () => { clearInterval(timer); timer = null; };

        dots.forEach((d) =>
          d.addEventListener('click', () => {
            show(parseInt(d.dataset.heroDot, 10) || 0);
            stop(); start();          // restart the cycle after a manual pick
          })
        );

        // Don't animate offscreen or in a hidden tab
        root.addEventListener('mouseenter', stop);
        root.addEventListener('mouseleave', start);
        root.addEventListener('focusin', stop);
        root.addEventListener('focusout', start);
        document.addEventListener('visibilitychange', () =>
          document.hidden ? stop() : start()
        );

        if ('IntersectionObserver' in window) {
          new IntersectionObserver(
            ([e]) => (e.isIntersecting ? start() : stop()),
            { threshold: 0.15 }
          ).observe(root);
        } else {
          start();
        }
      });
    },
  };

  const ProductQuantity = {
    init() {
      document.addEventListener('click', (e) => {
        const minus = e.target.closest('[data-qty-minus]');
        const plus = e.target.closest('[data-qty-plus]');
        if (!minus && !plus) return;
        e.preventDefault();

        const wrapper = (minus || plus).closest('.product__quantity, [data-product-quantity]');
        if (!wrapper) return;
        const input = wrapper.querySelector('[data-qty-input], input[type="number"]');
        if (!input) return;

        let val = parseInt(input.value, 10) || 1;
        const min = parseInt(input.min, 10) || 1;
        if (minus) val = Math.max(min, val - 1);
        if (plus) val = val + 1;
        input.value = val;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },
  };

  /* ==========================================================================
     INIT — DOMContentLoaded
     ========================================================================== */

  document.addEventListener('DOMContentLoaded', () => {
    Header.init();
    CartDrawer.init();
    VariantSelector.init();
    ProductTabs.init();
    ProductGallery.init();
    Accordion.init();
    NewsletterForm.init();
    QuickView.init();
    AnnouncementBar.init();
    LazyLoad.init();
    SmoothScroll.init();
    Toast.init();
    CurrencySelector.init();
    MobileStickyATC.init();
    CollectionFilters.init();
    AddToCartForm.init();
    ProductQuantity.init();
    ScrollReveal.init();
    Rail.init();
    StickyStory.init();
    CountUp.init();
    Parallax.init();
    Marquee.init();
    HeroCarousel.init();
  });

  /* Expose key utilities on the global namespace for other scripts */
  window.Carvica = window.Carvica || {};
  window.Carvica.formatMoney = formatMoney;
  window.Carvica.CartDrawer  = CartDrawer;
  window.Carvica.Toast       = Toast;
  window.Carvica.Header      = Header;
})();
